import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, mediaUrl } from "@/lib/api";
import { Field, FileField, inputCls } from "./fields";

const EMPTY = {
  title: "", cover_url: "", preview_url: "", lyrics: "", about: "",
  spotify_url: "", apple_url: "", album_id: "", is_single: true,
  release_date: "", order: 0, featured: true,
};

export default function Releases() {
  const [items, setItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [editing, setEditing] = useState(null);
  const [countdown, setCountdown] = useState({ enabled: false, release_id: "", headline: "", message: "", target: "" });
  const [cdSaving, setCdSaving] = useState(false);
  const [fanCount, setFanCount] = useState(null);

  const load = () => {
    api.get("/admin/releases").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/albums").then((r) => setAlbums(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      if (r.data?.countdown) setCountdown({ enabled: false, release_id: "", headline: "", message: "", target: "", ...r.data.countdown });
    }).catch(() => {});
    api.get("/admin/subscribers").then((r) => setFanCount(r.data.count)).catch(() => {});
  }, []);

  const setCd = (patch) => setCountdown((c) => ({ ...c, ...patch }));

  const saveCountdown = async () => {
    setCdSaving(true);
    try {
      await api.put("/admin/settings", { countdown });
      toast.success("Countdown saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCdSaving(false);
    }
  };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/releases/${editing.id}`, editing);
      else await api.post("/admin/releases", editing);
      toast.success("Release saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this release?")) return;
    await api.delete(`/admin/releases/${id}`);
    load();
  };

  return (
    <div data-testid="admin-releases">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-slate-50">Releases</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-gold !px-5 !py-2 text-xs" data-testid="release-new-btn">
          <Plus size={14} /> New Release
        </button>
      </div>

      <div className="rounded-2xl border border-gold-400/20 bg-navy-900/50 p-6 mb-8" data-testid="countdown-settings">
        <h2 className="font-serif text-xl text-slate-50 mb-1">Release Countdown Banner</h2>
        <p className="text-xs text-slate-400 font-light mb-6 max-w-2xl">
          Announce a new release before it joins the catalogue below. Shows at the top of the Music page and above
          Featured Music on the homepage, with cover art, your teaser words, a live countdown and the 60-second
          preview. Hidden completely when switched off.
        </p>
        <label className="flex items-center gap-3 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={countdown.enabled}
            onChange={(e) => setCd({ enabled: e.target.checked })}
            className="w-4 h-4 accent-gold-400"
            data-testid="countdown-enabled-toggle"
          />
          <span className="text-sm text-slate-200">Show countdown banner on the site</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Release or album">
            <select
              className={inputCls}
              value={countdown.release_id}
              onChange={(e) => setCd({ release_id: e.target.value })}
              data-testid="countdown-release-select"
            >
              <option value="" className="bg-navy-900">Choose a release or album…</option>
              {albums.length > 0 && (
                <optgroup label="Albums" className="bg-navy-900">
                  {albums.map((al) => (
                    <option key={al.id} value={`album:${al.id}`} className="bg-navy-900">{al.title}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Tracks" className="bg-navy-900">
                {items.map((t) => (
                  <option key={t.id} value={t.id} className="bg-navy-900">{t.title}</option>
                ))}
              </optgroup>
            </select>
          </Field>
          <Field label="Release date & time (your local time)">
            <input type="datetime-local" className={inputCls} value={countdown.target}
              onChange={(e) => setCd({ target: e.target.value })} data-testid="countdown-target-input" />
          </Field>
          <Field label="Headline (optional — defaults to the track title)">
            <input className={inputCls} value={countdown.headline} placeholder="New single incoming"
              onChange={(e) => setCd({ headline: e.target.value })} data-testid="countdown-headline-input" />
          </Field>
          <Field label="Teaser words (optional)">
            <textarea className={`${inputCls} min-h-20`} value={countdown.message} placeholder="A song about… out Friday."
              onChange={(e) => setCd({ message: e.target.value })} data-testid="countdown-message-input" />
          </Field>
        </div>
        <button onClick={saveCountdown} disabled={cdSaving} className="btn-gold !px-6 !py-2.5 text-xs disabled:opacity-50 mt-5" data-testid="countdown-save-btn">
          {cdSaving ? "Saving…" : "Save Countdown"}
        </button>
        {fanCount !== null && (
          <p className="text-xs text-slate-500 mt-4" data-testid="subscriber-count">
            {fanCount} {fanCount === 1 ? "fan has" : "fans have"} signed up for launch emails on the countdown banner —
            they're emailed automatically the moment the countdown reaches zero.
          </p>
        )}
      </div>

      {editing && (
        <div className="rounded-2xl border border-gold-400/25 bg-navy-900/70 p-6 mb-8 space-y-4" data-testid="release-form">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-lg text-slate-100">{editing.id ? "Edit Release" : "New Release"}</h2>
            <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-200" data-testid="release-form-close"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="release-title-input" /></Field>
            <Field label="Release date"><input type="date" className={inputCls} value={editing.release_date}
              onChange={(e) => setEditing({ ...editing, release_date: e.target.value })} data-testid="release-date-input" /></Field>
            <Field label="Spotify URL"><input className={inputCls} value={editing.spotify_url}
              onChange={(e) => setEditing({ ...editing, spotify_url: e.target.value })} data-testid="release-spotify-input" /></Field>
            <Field label="Apple Music URL"><input className={inputCls} value={editing.apple_url}
              onChange={(e) => setEditing({ ...editing, apple_url: e.target.value })} data-testid="release-apple-input" /></Field>
            <Field label="Album">
              <select className={inputCls} value={editing.album_id}
                onChange={(e) => setEditing({ ...editing, album_id: e.target.value, is_single: !e.target.value })} data-testid="release-album-select">
                <option value="" className="bg-navy-900">Single (no album)</option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id} className="bg-navy-900">{a.title}</option>
                ))}
              </select>
            </Field>
            <Field label="Catalogue position (0 = next free number at the end; positions never shift)"><input type="number" className={inputCls} value={editing.order}
              onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} data-testid="release-order-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FileField label="Cover art" accept="image/*" value={editing.cover_url}
              onChange={(url) => setEditing({ ...editing, cover_url: url })} testid="release-cover-upload" />
            <FileField label="60s preview (MP3)" accept="audio/*" value={editing.preview_url}
              onChange={(url) => setEditing({ ...editing, preview_url: url })} testid="release-audio-upload" />
          </div>
          <Field label="Lyrics"><textarea rows={6} className={inputCls} value={editing.lyrics}
            onChange={(e) => setEditing({ ...editing, lyrics: e.target.value })} data-testid="release-lyrics-input" /></Field>
          <Field label="About this track"><textarea rows={3} className={inputCls} value={editing.about}
            onChange={(e) => setEditing({ ...editing, about: e.target.value })} data-testid="release-about-input" /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" className="accent-[#D4AF37]" checked={editing.featured}
              onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="release-featured-check" />
            Featured on homepage
            <span className="text-[11px] text-slate-500" data-testid="featured-slots-label">
              ({items.filter((r) => r.featured).length}/8 slots used — new launches take a slot automatically; when full, the longest-featured steps back)
            </span>
          </label>
          <button onClick={save} className="btn-gold !px-6 !py-2.5 text-xs" data-testid="release-save-btn">Save Release</button>
        </div>
      )}

      <div className="space-y-2">
        {[...items]
          .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || a.order - b.order)
          .map((r) => (
          <div key={r.id} className={`flex items-center gap-4 rounded-xl border px-4 py-3 ${r.featured ? "border-gold-400/30 bg-gold-400/5" : "border-white/5 bg-navy-900/50"}`} data-testid={`release-row-${r.id}`}>
            {r.cover_url && <img src={mediaUrl(r.cover_url)} alt="" className="w-10 h-10 rounded-md object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-100 truncate">{r.title}</p>
              <p className="text-[11px] text-slate-500">
                {r.is_single ? "Single" : "Album track"} · position {r.order}
                {r.featured && <span className="text-gold-400 ml-2">Featured</span>}
              </p>
            </div>
            <button onClick={() => setEditing({ ...EMPTY, ...r })} className="text-slate-400 hover:text-gold-300 p-2" data-testid={`release-edit-${r.id}`}><Pencil size={15} /></button>
            <button onClick={() => del(r.id)} className="text-slate-500 hover:text-red-400 p-2" data-testid={`release-delete-${r.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500">No releases yet.</p>}
      </div>
    </div>
  );
}
